import { Button, Card, Stack, Typography } from '@mui/joy';
import QuestionBubbleIcon from 'jsx:../../assets/icons/question-square-bubble.svg';

import { usePermissionsContext } from '../../context/permissionsContext';
import clearfeed from '../../services/support/clearfeed';

import { DEFAULT_CTA_LABEL, DEFAULT_SUBTITLE, DEFAULT_TITLE, VARIANTS } from './constants';
import type { AskAQuestionCardProps } from './types';

const AskAQuestionCard: React.FC<AskAQuestionCardProps> = ({
  prefill,
  title = DEFAULT_TITLE,
  subtitle = DEFAULT_SUBTITLE,
  ctaLabel = DEFAULT_CTA_LABEL,
  width,
  variant = 'default',
}) => {
  // Per the Figma note on frame 4:2 — question prompts are paid-only.
  const { isFreemiumUser } = usePermissionsContext();
  if (isFreemiumUser) return null;

  const style = VARIANTS[variant];

  // Figma node 1837:251260 (Questions card, project shell — outlined variant):
  //   card r=8, padding 16, gap 16
  //   title  : 16/700 lh=21.28 #171A1C
  //   label  : 12/500 lh=15.96 #555E68
  //   button : 14/400 lh=14    #4D4D4D, border #056B0A 2px, r=8, h=32, px=12, gap=4
  const isOutlined = variant === 'outlined';

  return (
    <Card
      variant={style.container.variant}
      sx={{
        p: style.container.padding,
        gap: style.outerGap,
        flexDirection: 'row',
        alignItems: 'center',
        borderRadius: (theme) => (isOutlined ? '8px' : theme.radius.sm),
        ...(style.container.variant === 'plain' && {
          background: 'transparent',
          boxShadow: 'none',
        }),
        ...(width !== undefined ? { width, maxWidth: '100%', flexShrink: 0 } : { width: '100%' }),
      }}
    >
      {style.icon.show && (
        <QuestionBubbleIcon
          width={style.icon.size}
          height={style.icon.size}
          style={{ flexShrink: 0 }}
        />
      )}
      <Stack
        direction={style.text.direction}
        alignItems={style.text.alignItems}
        gap={style.text.innerGap}
        flex={1}
      >
        <Typography
          level={style.title.level}
          fontWeight={'xl'}
          sx={{
            color: (theme) => theme.palette.text.primary,
            ...(isOutlined && { fontSize: '16px', lineHeight: '21.28px' }),
          }}
        >
          {title}
        </Typography>
        <Typography
          fontSize={style.subtitle.fontSize}
          fontWeight={style.subtitle.fontWeight}
          lineHeight={isOutlined ? '15.96px' : 1.42}
          noWrap={style.subtitle.noWrap}
          sx={{ color: (theme) => theme.palette.text.tertiary }}
        >
          {subtitle}
        </Typography>
      </Stack>
      <Button
        variant={style.button.variant}
        color={style.button.color}
        size='sm'
        onClick={() => clearfeed.open(prefill)}
        sx={{
          textWrap: 'nowrap',
          height: 32,
          borderRadius: (theme) => (isOutlined ? '8px' : theme.radius.sm),
          fontWeight: isOutlined ? 400 : 'md',
          ...(isOutlined && {
            fontSize: '14px',
            lineHeight: '14px',
            px: '12px',
            gap: '4px',
          }),
        }}
      >
        {ctaLabel}
      </Button>
    </Card>
  );
};

export { AskAQuestionCard };
export type { AskAQuestionVariant } from './types';
